import Spinner from "presentation/components/Shared/Spinner";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { Column } from "primereact/column";
import {
  DataTable,
  DataTableFilterEvent,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";

import { httpClient } from "infra/axios/axios-api";
import { Document } from "domain/entities/document";
import { EMAIL } from "domain/enum/email";
import { MessageTypeEnum } from "domain/enum/messageType";
import { DocumentInputFilter } from "domain/models/document-input-filter";
import { translateText } from "infra/translation/translate";
import {
  useDeleteCPSDocumentMutation,
  useDocumentByFilter,
} from "presentation/hooks/use-query-document";
import { useFilterInvoiceGridStore } from "presentation/stores/use-filter-invoiceGrid-store";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import React from "react";
import DocumentTypeDropdown from "./DocumentTypeDropdown";
import HeaderInvoiceGrid from "./HeaderInvoiceGrid";
import StatusInvoiceDropdown from "./StatusInvoiceDropdown";
import { Dialog } from "primereact/dialog";
import ApprovalForm from "./ApprovalForm";
import { InvoiceStatusEnum } from "domain/enum/invoiceStatus";
import { Tooltip } from "primereact/tooltip";
import PdfViewerDialog from "presentation/components/Shared/PdfViewerDialog";

interface InvoiceGridProps {
  externalFilters?: {
    employeeId?: string;
    invoiceMonth: number | undefined;
    invoiceYear: number | undefined;
    sortBy: string;
    sortOrder: string;
  };
  hideAddInvoice: boolean;
}

const InvoiceGrid: React.FC<InvoiceGridProps> = ({
  externalFilters,
  hideAddInvoice,
}) => {
  const confirmAction = (message: string, acceptCallback: () => void) => {
    confirmDialog({
      message: message,
      header: "Confirmação",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: acceptCallback,
    });
  };

  const showMessage = (
    type: MessageTypeEnum,
    subject: string,
    message: string
  ) => {
    toast.current?.show({
      severity: type,
      summary: subject,
      detail: message,
      life: 2500,
    });
  };

  const toast = useRef<Toast>(null);

  const { userEmployee } = useAuthStore();
  const { filters, setFilters } = useFilterInvoiceGridStore();

  const [lazyParams, setLazyParams] = useState({
    sortBy: "createdAt",
    sortOrder: "desc",
    limit: 50,
    page: 1,
    filters: {} as any,
  });
  const [showModal, setShowModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(
    null
  );
  const [s3PathToDisplay, setS3PathToDisplay] = useState<string | null>(null);
  const [docUrl, setDocUrl] = useState<string | null>(null);
  const [showDoc, setShowDoc] = useState(false);

  const {
    data: documents,
    isLoading,
    isError,
    error,
    refetch,
  } = useDocumentByFilter({
    sortBy: lazyParams.sortBy,
    sortOrder: lazyParams.sortOrder,
    page: lazyParams.page,
    limit: lazyParams.limit,
    justCPSdocs: true,
    ...lazyParams.filters,
  } as DocumentInputFilter);

  const {
    mutate: deleteCPSDocumentMutation,
    isSuccess: isDeleteCPSDocSuccess,
    isError: isDeleteCPSDocError,
    isPending: isDeleteCPSDocPending,
  } = useDeleteCPSDocumentMutation();

  useEffect(() => {
    const filterObject = Object.fromEntries(
      Object.entries(filters)
        .map(([key, value]) => {
          if ("constraints" in value) {
            return [key, value.constraints?.[0]?.value ?? ""];
          }
          return [key, (value as DataTableFilterMetaData)?.value ?? ""];
        })
        .filter(
          ([_, value]) => value !== null && value !== undefined && value !== ""
        )
    );

    const employeeIdForQuery =
      externalFilters?.employeeId === "*" ||
      externalFilters?.employeeId === "todos"
        ? undefined
        : externalFilters?.employeeId ?? userEmployee?.id;

    const mergedFilters = {
      ...filterObject,
      ...(externalFilters || {}),
      employeeId: employeeIdForQuery,
    };

    setLazyParams((prev) => ({
      ...prev,
      filters: mergedFilters,
      sortBy: externalFilters?.sortBy || prev.sortBy,
      sortOrder: externalFilters?.sortOrder || prev.sortOrder,
    }));
  }, [filters, externalFilters, userEmployee]);

  useEffect(() => {
    if (isDeleteCPSDocSuccess) {
      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Documento eliminado com sucesso",
        life: 1500,
      });
      refetch();
    }
  }, [isDeleteCPSDocSuccess]);

  useEffect(() => {
    if (isDeleteCPSDocError) {
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Documento não eliminado",
        life: 1500,
      });
    }
  }, [isDeleteCPSDocError]);

  if (isLoading) return <Spinner />;

  if (isError && toast.current) {
    toast.current.show({
      severity: "error",
      summary: "Erro",
      detail: error?.message || "Ocorreu um erro inesperado",
      life: 1500,
    });
  }

  const getDocumentFromContabo = async (data: Document) => {
    if (data) {
      let s3Path: string | undefined = "";
      if (data.fullPath) {
        s3Path = data.fullPath;
      }

      if (s3Path) {
        setS3PathToDisplay(s3Path);
        // const signedUrlResponse = await axios.get(
        //   `${process.env.REACT_APP_BASE_URL}/assets/${s3Path}`
        // );

        // const signedUrl = signedUrlResponse.data.url;
        // if (signedUrl) {
        //   setDocUrl(signedUrl);
        //   setShowDoc(true);
        // } else {
        //   showMessage(
        //     MessageTypeEnum.ERROR,
        //     "Relatório",
        //     "Não foi possível obter URL do relatório."
        //   );
        // }
      }
    }
  };

  const removeDocument = async (docData: Document) => {
    if (!docData?.id || !docData.fullPath) return;

    try {
      await httpClient.delete(
        `/assets/invoices/${userEmployee?.id}`,
        {
          data: { filePath: docData.fullPath },
        }
      );

      const currentDocument: Document = {
        id: docData.id,
        documentType: { id: docData.documentType.id },
        employee: { id: userEmployee?.id || "" },
        fullPath: docData.fullPath,
        lastModifiedBy: userEmployee?.emailAddress ?? EMAIL,
      };

      deleteCPSDocumentMutation(currentDocument);
    } catch (err) {
      console.error("Delete file failed:", err);
    }
  };

  const showActionOptions = (rowData: Document) => {
    const possibleDeleteStatus = [InvoiceStatusEnum.ENVIADO.toString()];

    if (!hideAddInvoice) {
      return (
        <div className="spacing-icons">
          {
            <i
              className="pi pi-eye cursor-pointer"
              onClick={async () => await getDocumentFromContabo(rowData)}
            />
          }
          {possibleDeleteStatus.includes(rowData?.invoice?.status ?? "") && (
            <i
              className="pi pi-trash cursor-pointer"
              onClick={() =>
                confirmAction(
                  "Deseja anular a submissão deste documento?",
                  async () => await removeDocument(rowData)
                )
              }
            />
          )}
        </div>
      );
    }

    return (
      <div className="spacing-icons">
        {possibleDeleteStatus.includes(rowData?.invoice?.status ?? "") && (
          <i
            className="pi pi-file-check cursor-pointer"
            style={{ color: "var(--primary-color)" }}
            onClick={() => {
              setSelectedDocument(rowData);
              setShowModal(true);
            }}
          />
        )}
        {!possibleDeleteStatus.includes(rowData?.invoice?.status ?? "") && (
          <i
            className="pi pi-eye cursor-pointer"
            onClick={() => {
              setSelectedDocument(rowData);
              setShowModal(true);
            }}
          />
        )}
      </div>
    );
  };

  const handleCloseModal = async () => {
    setShowModal(false);
    setSelectedDocument(null);
    refetch();
  };

  if (isDeleteCPSDocPending || isLoading) return <Spinner />;

  return (
    <div>
      <DataTable
        value={documents?.data || []}
        lazy
        paginator
        first={(lazyParams.page - 1) * lazyParams.limit}
        rows={lazyParams.limit}
        totalRecords={documents?.pagination?.count || 0}
        loading={isLoading}
        onPage={(e: DataTablePageEvent) =>
          setLazyParams({
            ...lazyParams,
            page: e.page ? e.page + 1 : 1,
            limit: e.rows,
          })
        }
        sortField={lazyParams.sortBy}
        sortOrder={lazyParams.sortOrder === "asc" ? 1 : -1}
        onSort={(e: DataTableSortEvent) =>
          setLazyParams({
            ...lazyParams,
            sortBy: e.sortField,
            sortOrder:
              e.sortOrder === -1 ? "desc" : e.sortOrder === 1 ? "asc" : "",
          })
        }
        onFilter={(e: DataTableFilterEvent) => setFilters(e.filters)}
        filters={filters}
        size="small"
        resizableColumns
        columnResizeMode="expand"
        header={<HeaderInvoiceGrid hideAddInvoice={hideAddInvoice} />}
        rowsPerPageOptions={[5, 10, 25, 50]}
        paginatorLeft
        dataKey="id"
      >
        <Column
          field="employee.name"
          header={translateText("Employee", "pt")}
          sortable
          filterField="employeeName"
          filter
          showFilterMatchModes={false}
        />
        <Column
          field="documentType.name"
          header={translateText("Document Type", "pt")}
          sortable
          filter
          filterField="documentTypeName"
          showFilterMatchModes={false}
          filterElement={(options) => (
            <DocumentTypeDropdown options={options} />
          )}
        />
        <Column
          field="invoice.year"
          header={translateText("Year", "pt")}
          sortable
          filter
          filterField="invoiceYear"
          showFilterMatchModes={false}
        />
        <Column
          field="invoice.month"
          header={translateText("Month", "pt")}
          sortable
          filter
          filterField="invoiceMonth"
          showFilterMatchModes={false}
        />
        <Column
          field="invoice.status"
          header={translateText("Status", "pt")}
          sortable
          filter
          filterField="invoiceStatus"
          body={(rowData) => (
            <div className="flex align-items-center gap-2">
              {rowData.invoice.status}
              {rowData.invoice.status === InvoiceStatusEnum.NAO_APROVADO && (
                <>
                  <i
                    id={`tooltip-${rowData.id}`}
                    className="pi pi-info-circle cursor-pointer"
                  />
                  <Tooltip
                    target={`#tooltip-${rowData.id}`}
                    content={`Motivo para a não aprovação: ${
                      rowData?.invoice?.rejectionReason ?? "-"
                    }`}
                    appendTo={document.body}
                  />
                </>
              )}
            </div>
          )}
          showFilterMatchModes={false}
          filterElement={(options) => (
            <StatusInvoiceDropdown options={options} />
          )}
        />

        <Column
          body={showActionOptions}
          header={translateText("Actions", "pt")}
          align="center"
          alignHeader="center"
        />
      </DataTable>

      <Dialog
        //header={`Documento a aprovar`}
        visible={showModal}
        style={{ width: "80vw", height: "90vh" }}
        onHide={handleCloseModal}
      >
        <ApprovalForm
          // url={url}
          document={selectedDocument}
          onClose={handleCloseModal}
          showMessage={showMessage}
        />
      </Dialog>

      <Dialog
        header="Visualização do Documento"
        visible={showDoc}
        style={{ width: "80vw", height: "90vh" }}
        onHide={() => {
          setShowDoc(false);
          setDocUrl(null);
        }}
      >
        {docUrl &&
          (docUrl.includes(".pdf") ? (
            <iframe
              src={`${docUrl}#navpanes=0`}
              width="100%"
              height="700px"
              title="Invoice"
              style={{ border: "none" }}
            ></iframe>
          ) : (
            <img
              src={docUrl}
              width="100%"
              height="auto"
              title="Invoice"
              style={{ border: "none" }}
            ></img>
          ))}
      </Dialog>

      <Toast ref={toast} position="bottom-right" />

      <ConfirmDialog />
      {s3PathToDisplay && (
        <PdfViewerDialog
          s3Path={s3PathToDisplay}
          onHide={() => setS3PathToDisplay(null)}
        />
      )}
    </div>
  );
};

export default InvoiceGrid;
